import { WeekdayNumbers } from './types/WeekdayNumbers';
import { DateTime } from './types/DateTime';
import { Duration } from './types/Duration';
import { WeekdayTimeQuotas } from '../../types/api';

export const weekendDays: (keyof WeekdayTimeQuotas)[] = ['sat', 'sun'];
export const weekdayDays: (keyof WeekdayTimeQuotas)[] = [
  'mon',
  'tue',
  'wed',
  'thr',
  'fri',
];

export const isWeekend = (day: keyof WeekdayTimeQuotas) =>
  weekendDays.includes(day);

const weekdayNumberToWeekdayValue = (weekDayNumber: WeekdayNumbers) => {
  const weekdayValues: { [idx in WeekdayNumbers]: keyof WeekdayTimeQuotas } = {
    1: 'mon',
    2: 'tue',
    3: 'wed',
    4: 'thr',
    5: 'fri',
    6: 'sat',
    7: 'sun',
  };

  if (!weekdayValues[weekDayNumber]) {
    throw new Error(`Invalid weekday number: ${weekDayNumber}`);
  }

  return weekdayValues[weekDayNumber];
};

/**
 * Get the weekday value for a given date
 * @param date - the date in milliseconds
 */
export const getWeekdayValue = (
  weekdayValues: WeekdayTimeQuotas,
  date: number,
  accountTimezone: string
) => {
  return weekdayValues[
    weekdayNumberToWeekdayValue(getWeekdayForTimezone(date, accountTimezone))
  ];
};

const checkDate = (dt: DateTime) => {
  if (!dt.isValid) {
    throw new Error(
      `Invalid date: ${dt}\n${dt.invalidReason}: ${dt.invalidExplanation}`
    );
  }

  return dt;
};

/**
 * Get the DateTime for a given date
 *
 * @param date - the date in milliseconds o a Date object
 * @param timezone - the timezone to use
 * @example
 * ```typescript
 * getDateTime(1495750000000, 'utc')
 * ```
 */
export const getDateTime = (
  date: Date | number,
  timezone?: string
): DateTime => {
  const dt = DateTime.fromMillis(
    date instanceof Date ? date.getTime() : date,
    timezone
      ? {
          zone: timezone,
        }
      : undefined
  );

  return checkDate(dt);
};

/**
 * Get the weekday for a given date
 *
 * @param date - the date in milliseconds or a Date object
 * @param timezone - the timezone to use
 * @example
 * getWeekdayForTimezone(1495750000000, 'utc') // 4
 */
const getWeekdayForTimezone = (date: Date | number, timezone?: string) =>
  getDateTime(date, timezone).weekday;

/**
 * Create a DateTime from an ISO 8601 string
 *
 * @param text - the ISO string
 * @param opts - options to affect the creation
 * @param timezone - use this zone if no offset is specified in the input string itself. Will also convert the time to this zone. Defaults to 'local'.
 * @example
 * getDateTimeFromISO('2016-05-25T09:08:34.123')
 * @example
 * getDateTimeFromISO('2016-05-25T09:08:34.123+06:00')
 * @example
 * getDateTimeFromISO('2016-05-25T09:08:34.123', 'utc')
 */
export const getDateTimeFromISO = (date: string, timezone?: string) =>
  checkDate(DateTime.fromISO(date, timezone ? { zone: timezone } : undefined));

export const getCurrentDateTime = (timezone?: string) =>
  DateTime.local({ zone: timezone });

/**
 * Get the day before a given DateTime
 */
export const yesterday = (dt?: DateTime) =>
  dt ? dt.minus({ days: 1 }) : getCurrentDateTime().minus({ days: 1 });

/**
 * Get a date in the future
 */
export const getDateInFuture = (
  {
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  }: { days?: number; hours?: number; minutes?: number; seconds?: number },
  dt?: DateTime
) =>
  dt
    ? dt.plus({ days, hours, minutes, seconds })
    : getCurrentDateTime().plus({ days, hours, minutes, seconds });

/**
 * Get the day of the year
 *
 * @example
 * getDayOfTheYear(DateTime.local(2017, 5, 25)).ordinal // 145
 */
export const getDayOfTheYear = (dt: DateTime) => dt.ordinal;

export const getISOfromDateTime = (dt: DateTime) => dt.toUTC().toISO();

export const getDateMs = (dt: DateTime) => dt.toMillis();

export const now = () => {
  return getDateMs(getCurrentDateTime());
};

/*
 * Timestamp in seconds.
 *
 * Rounded down to the nearest integer.
 */
export const nowInSeconds = () => Math.floor(now() / 1000);

/**
 * Get the hour of a given date
 *
 * @param date - the date in milliseconds or a Date object
 * @param timezone - the timezone to use
 * @example
 * getHour(1495750000000, 'utc') // 9
 */
export const getHour = (date: Date | number, timezone?: string) =>
  getDateTime(date, timezone).hour;

/**
 * Returns if the two provided DateTimes have the same calendar day.
 */
export const isSameDay = (dt: DateTime, otherDt: DateTime) =>
  dt.hasSame(otherDt, 'day');

/**
 * Get the start of the day for a given DateTime.
 *
 * @returns DateTime with the same calendar day, but with time set to 00:00:00.000
 */
export const getStartOfDay = (dt: DateTime) => dt.startOf('day');

export const getDurationFromMs = (ms: number) => Duration.fromMillis(ms);

/**
 * Returns a time string (hh:mm:ss.SSS) for the provided Duration
 *
 * @remarks
 * After 1 day, it will keep increasing the hours instead of representing the days.
 */
export const getTimeStringFromDuration = (duration: Duration) =>
  duration.toFormat('hh:mm:ss.SSS');

export const getMsFromSeconds = (seconds: number) =>
  Duration.fromObject({ seconds }).toMillis();

/**
 * Convert milliseconds to seconds (with round down).
 */
export const toSeconds = (millis: number) => Math.floor(millis / 1000);

/**
 * Get the system timezone.
 *
 * @remarks This is NOT the account's timezone,
 * but the timezone set on the device system settings.
 * In case of tampering, it won't be the real one.
 */
export const getSystemTimezone = () => {
  return DateTime.now().zoneName;
};
